// Règles des frais et débours, sans accès réseau.
//   frais   : refacturé au nom du cabinet, montant HT + TVA.
//   débours : achat pour le compte du client, montant TTC exact du fournisseur, hors TVA.

export type ExpenseNature = 'frais' | 'debours';

export const EXPENSE_NATURE_LABELS: Record<ExpenseNature, string> = {
  frais: 'Frais',
  debours: 'Débours',
};

const cents = (v: number) => Math.round((v + Number.EPSILON) * 100);

/** `amount` = HT pour un frais, TTC pour un débours. */
export function expenseAmounts(input: { nature: ExpenseNature; amount: number; vat_rate: number }): {
  amount_ht: number;
  vat_rate: number;
  amount_ttc: number;
} {
  if (input.nature === 'debours') {
    const ttc = cents(input.amount) / 100;
    return { amount_ht: ttc, vat_rate: 0, amount_ttc: ttc };
  }
  const htCents = cents(input.amount);
  const vatCents = cents((htCents / 100) * (input.vat_rate / 100));
  return { amount_ht: htCents / 100, vat_rate: input.vat_rate, amount_ttc: (htCents + vatCents) / 100 };
}

export interface ExpenseDraft {
  nature: ExpenseNature;
  piece_date: string;
  label: string;
  supplier: string;
  piece_number: string;
  amount: number;
}

export type ExpenseErrors = Partial<Record<'piece_date' | 'label' | 'supplier' | 'piece_number' | 'amount', string>>;

export function validateExpense(draft: ExpenseDraft): ExpenseErrors {
  const errors: ExpenseErrors = {};
  if (draft.nature === 'debours') {
    if (!draft.supplier.trim()) errors.supplier = 'Le fournisseur est obligatoire pour un débours.';
    if (!draft.piece_number.trim()) errors.piece_number = 'Le numéro de pièce est obligatoire pour un débours.';
  } else if (!draft.label.trim()) {
    errors.label = 'Le libellé est obligatoire.';
  }
  if (!draft.piece_date) errors.piece_date = 'La date de la pièce est obligatoire.';
  if (!(Number(draft.amount) > 0)) errors.amount = 'Le montant doit être supérieur à zéro.';
  return errors;
}

/** Jours écoulés depuis la date de pièce (ancienneté de l'avance). */
export function expenseAge(pieceDate: string, now: Date): number {
  const start = new Date(`${pieceDate.slice(0, 10)}T00:00:00`);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((today.getTime() - start.getTime()) / 86_400_000));
}

/** Mandat de débours proposé à partir de la convention d'honoraires signée. */
export function mandatePrefill(signedAt: string | null): { on: string | null; ref: string | null } {
  if (!signedAt) return { on: null, ref: null };
  const [y, m, d] = signedAt.slice(0, 10).split('-');
  return { on: signedAt.slice(0, 10), ref: `Convention d’honoraires du ${d}/${m}/${y}` };
}
